import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "./ThemeToggle";
import { useAuth } from "../context/AuthContext";
import { LOGO } from "../lib/brand";

const LINKS = [
  { to: "/programs", label: "Programs" },
  { to: "/videos", label: "Videos" },
  { to: "/blog", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${scrolled ? "bg-background/85 backdrop-blur-xl border-border" : "bg-transparent border-transparent"}`}>
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8 h-[70px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <img src={LOGO} alt="Being Fit For You" className="h-11 w-11 rounded-lg bg-black object-contain p-1" />
          <span className="font-display text-2xl md:text-[26px] font-800 uppercase tracking-tight leading-none hidden sm:block">
            Being<span className="text-primary">Fit</span>ForYou
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-xs uppercase tracking-[0.15em] font-bold transition-colors hover:text-primary ${location.pathname === l.to ? "text-primary" : ""}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          {user ? (
            <button data-testid="nav-dashboard" onClick={() => navigate("/app")}
              className="hidden sm:flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-bold hover:scale-[1.03] transition-transform">
              <LayoutDashboard size={14} /> Dashboard
            </button>
          ) : (
            <>
              <button data-testid="nav-login" onClick={() => navigate("/login")}
                className="hidden sm:block text-xs uppercase tracking-[0.12em] font-bold hover:text-primary transition-colors">Login</button>
              <button data-testid="nav-start" onClick={() => navigate("/assessment")}
                className="hidden sm:block bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-bold hover:scale-[1.03] transition-transform">
                Start Free
              </button>
            </>
          )}
          <button className="lg:hidden" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-background border-b border-border">
            <div className="flex flex-col px-6 py-6 gap-4">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="font-display text-3xl uppercase tracking-tight">{l.label}</Link>
              ))}
              <div className="flex gap-3 pt-3">
                {user ? (
                  <Link to="/app" className="bg-primary text-primary-foreground px-6 py-3 rounded-full text-xs uppercase font-bold tracking-widest">Dashboard</Link>
                ) : (
                  <>
                    <Link to="/login" className="border border-border px-6 py-3 rounded-full text-xs uppercase font-bold tracking-widest">Login</Link>
                    <Link to="/assessment" className="bg-primary text-primary-foreground px-6 py-3 rounded-full text-xs uppercase font-bold tracking-widest">Start Free</Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
